import { Dialog } from "primereact/dialog"
import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useActionRule } from "../hooks/use-actionRuleDate"
import {
     staleInactivePriceAdjustmentIds,
     withoutInactivePriceAdjustments,
     availableActionCategories,
} from "../utils/priceAdjustmentModal"
import { PPPriceManagementActionRuleTile } from "./action-rule/actionRuleForms"

function PPPriceManagementActionRuleModal({visible, close, editingRule, marginOnly = false}) {
     const [t] = useTranslation();
     const { ActionRuleHelper, PriceChangeHelper, PriceChangeMarginHelper } = useActionRule();
     const [selectedRule, setSelectedRule] = useState(null);
     const [saveAction, setSaveAction] = useState(null);
     const [canSave, setCanSave] = useState(false);
     const draftSnapshotRef = useRef(null);
     const shouldRestoreDraftRef = useRef(false);

    const categories = [
          {label: "Maximale Preisangleichung", id: 0},
          {label: "Preisangleichung", id: 1},
          {label: "Anpassungs-Intervall", id: 2},
          {label: "Preisrundung", id: 3},
          {label: "Preisanpassung nach Marge", id: 4},
          {label: "Kalkulation auf Kanal- und Regel-Preis", id: 5},
          {label: "Amazon BuyBox", id: 6},
    ].filter((category) => !marginOnly || category.id === 4);

     const getCategory = (keyString) => {
          const mapping = {
               'maxPriceAdjustment': 0,
               'priceChange': 1, 
               'changeInterval': 2,
               'roundPrice': 3,
               'priceChangeMargin': 4,
               'calculationChannelAndRulePrice': 5,
               'amazonBuyBox': 6
          };
          return mapping[keyString] ?? null;
     };
     const lockedCategoryId = editingRule ? getCategory(editingRule.keyString) : null;
     const activeCategoryIds = ActionRuleHelper.getActiveRules()
          .map((rule) => getCategory(rule.keyString))
          .filter((id) => id !== null);
     const availableCategories = availableActionCategories({
          categories,
          editingCategoryId: lockedCategoryId,
          activeCategoryIds,
          hasMarginComponent: PriceChangeMarginHelper.isActive(),
          hasPriceChangeComponent: PriceChangeHelper.hasActivePriceAdjustmentRules(),
     });
     const selectedStillAvailable = availableCategories.some((category) => category.id === selectedRule?.id)
     const displayedRule = marginOnly || selectedStillAvailable ? selectedRule : (availableCategories[0] ?? null)

    useEffect(() => {
         if (!visible) {
              return;
         }

         if (!draftSnapshotRef.current) {
              staleInactivePriceAdjustmentIds(PriceChangeHelper.getPriceAdjustmentRules(), null)
                   .forEach((ruleId) => PriceChangeHelper.removePriceAdjustmentRule(ruleId));
              draftSnapshotRef.current = withoutInactivePriceAdjustments(ActionRuleHelper.createDraftSnapshot());
              shouldRestoreDraftRef.current = true;
         }

         if (editingRule) {
              const categoryId = getCategory(editingRule.keyString);
              if (categoryId !== null) {
                   const category = categories.find(cat => cat.id === categoryId);
                   if (category) {
                        setSelectedRule(category);
                        return;
                   }
              }
         }

         if (marginOnly) {
              setSelectedRule(categories[0] ?? null);
              return;
         }

         setSelectedRule(availableCategories[0] ?? null);
   }, [visible, editingRule, marginOnly]);

    function closeModal(options) {
         const preserveDraft = options?.preserveDraft === true;
         if (!preserveDraft && shouldRestoreDraftRef.current && draftSnapshotRef.current) {
              ActionRuleHelper.restoreDraftSnapshot(draftSnapshotRef.current);
         }
         draftSnapshotRef.current = null;
         shouldRestoreDraftRef.current = false;
         setSelectedRule(null);
         setCanSave(false);
         close();
    }

     const handleSave = () => {
          if (saveAction && canSave) {
               saveAction();
               closeModal({ preserveDraft: true });
          }
     };

     return (
          <Dialog
               header={editingRule ? `Aktion-Regel bearbeiten - ${editingRule.name || editingRule.keyString}` : "Regel - Aktion"}
               footer={
                    <>
                         <button className="btn btn-danger btn-lg m-2" onClick={closeModal}>
                              {t("single.cancel")}
                         </button>
                         {displayedRule && (
                              <button
                                   className="btn btn-primary btn-lg m-2"
                                   onClick={handleSave}
                                   disabled={!canSave}
                              >
                                   <i className="fas fa-save me-2"></i>
                                   {editingRule ? 'Aktualisieren' : 'Speichern'}
                              </button>
                         )}
                    </>
               }
               visible={visible} 
               style={{ width: '60vw', height: '70vh' }}
              onHide={closeModal}
          >
               <div className="mb-3">
                    {marginOnly ? (
                         <h5 className="mb-0">Preisanpassung nach Marge</h5>
                    ) : availableCategories.length === 0 ? (
                         <div className="alert alert-info mb-0" role="alert">
                              Alle Aktions-Komponenten sind bereits in der Regel vorhanden.
                         </div>
                    ) : (
                         <div className="d-flex flex-wrap gap-2">
                              {availableCategories.map((category) => (
                                   <button
                                        key={category.id}
                                        type="button"
                                        className={`btn btn-sm ${displayedRule && displayedRule.id === category.id ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setSelectedRule(category)}
                                        disabled={lockedCategoryId !== null && category.id !== lockedCategoryId}
                                   >
                                        {category.label}
                                   </button>
                              ))}
                         </div>
                    )}
               </div>
               <PPPriceManagementActionRuleTile 
                    selectedRule={displayedRule}
                    editingRule={editingRule}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
               />
          </Dialog>
     );
}
export default PPPriceManagementActionRuleModal;

